import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';

import type { FC } from 'react';
import type { StreamChat } from 'stream-chat';

import { COUNTER_KEYS, INTERVIEW_KEYS, MODERATION_KEYS } from '@/constants/queryKeys';
import { connectStream, disconnectStream, isChatConfigured } from '@/services/chat';
import { createThrottle } from '@/utils/throttle';
import { errorUtils } from '@/services/errors';
import { readAccessToken, resolveSupabaseClient } from '@/services/supabase';
import { toEventUnread, toUnreadCount } from '@/utils/chat';
import { useAccess } from '@/hooks/access';
import { useStaffSession } from '@/hooks/auth';
import { LiveContext, useSubscription } from '@/hooks/live';

import type { TLiveProviderProps, TStaffEventPayload } from './LiveProvider.props';

const UNREAD_EVENTS = [
  'notification.message_new',
  'notification.mark_read',
  'notification.mark_unread',
  'message.new',
];

const CHAT_PERMISSION = 'messages:view';

const EVENTS_CHANNEL = 'staff-events';

const EVENTS_TABLE = 'staff_events';

const INVALIDATE_WAIT_MS = 300;

const POLL_INTERVAL_MS = 60_000;

const LIVE_QUERY_KEYS = [COUNTER_KEYS.root, MODERATION_KEYS.root, INTERVIEW_KEYS.root];

const LiveProvider: FC<TLiveProviderProps> = ({ children }) => {
  const { data: session } = useStaffSession();
  const { can } = useAccess();
  const queryClient = useQueryClient();

  const [client, setClient] = useState<StreamChat | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);

  const isConfigured = isChatConfigured();
  const isWanted = session?.stage === 'ready' && can(CHAT_PERMISSION) && isConfigured;

  useSubscription(() => {
    if (!isWanted) {
      return undefined;
    }

    let isActive = true;

    const open = async () => {
      const connection = await connectStream();

      if (!isActive || errorUtils.isNetworkError(connection)) {
        return;
      }

      setClient(connection);
      setTotalUnread(toUnreadCount(connection.user));
    };

    open();

    return () => {
      isActive = false;
      setClient(null);
      disconnectStream();
    };
  }, [isWanted]);

  useSubscription(() => {
    if (!client) {
      return undefined;
    }

    const listeners = UNREAD_EVENTS.map(name => client.on(name, (event) => {
      setTotalUnread(previous => toEventUnread(event, previous));
    }));

    return () => {
      listeners.forEach(listener => listener.unsubscribe());
    };
  }, [client]);

  useSubscription(() => {
    if (session?.stage !== 'ready') {
      return undefined;
    }

    const supabase = resolveSupabaseClient();
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let isActive = true;
    let hasJoined = false;

    const refresh = () => {
      LIVE_QUERY_KEYS.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    };

    const throttled = createThrottle(refresh, INVALIDATE_WAIT_MS);
    const poll = setInterval(refresh, POLL_INTERVAL_MS);

    const onEvent = (payload: TStaffEventPayload) => {
      if (payload.errors && payload.errors.length > 0) {
        return;
      }

      throttled.run();
    };

    const onStatus = (status: REALTIME_SUBSCRIBE_STATES) => {
      if (status !== REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
        return;
      }

      if (hasJoined) {
        throttled.run();
      }

      hasJoined = true;
    };

    const listen = async () => {
      const token = await readAccessToken();

      if (!isActive || !token) {
        return;
      }

      await supabase.realtime.setAuth(token);

      if (!isActive) {
        return;
      }

      channel = supabase
        .channel(EVENTS_CHANNEL)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: EVENTS_TABLE },
          onEvent,
        )
        .subscribe(onStatus);
    };

    listen();

    return () => {
      isActive = false;
      throttled.cancel();
      clearInterval(poll);

      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [session?.stage, queryClient]);

  const value = useMemo(() => ({
    client,
    totalUnread,
    isChatReady: client !== null,
    isChatConfigured: isConfigured,
  }), [client, totalUnread, isConfigured]);

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
};

export {
  LiveProvider,
};
